import { Icon } from '@loadsmart/miranda-react'

import { usePrefersReducedMotion } from 'core/ui/hooks/usePrefersReducedMotion'
import {
  formatLookup,
  formatRowsReturned,
  groupSamplePromptsByProduct,
  isMeasureColumn,
  analyticsMcpProductLabel,
  type AnalyticsMcpSample,
} from 'managed-analytics/domain/analyticsMcp'
import { useAnalyticsMcpPlayback } from 'managed-analytics/ui/hooks/useAnalyticsMcpPlayback'

import {
  StyledAnswer,
  StyledExchange,
  StyledFollowUp,
  StyledFollowUpAction,
  StyledFollowUpText,
  StyledPanel,
  StyledPanelBar,
  StyledPanelSpacer,
  StyledPanelTitle,
  StyledQuestion,
  StyledQuestionChip,
  StyledQuestionRow,
  StyledQuestionRowLabel,
  StyledQuestionRows,
  StyledReplayButton,
  StyledSampleBadge,
  StyledStatusLine,
  StyledStep,
  StyledSummary,
  StyledTable,
  StyledTableWrapper,
  StyledWorkingDots,
} from './AskPanel.styles'

function WorkingDots() {
  return (
    <StyledWorkingDots aria-hidden="true">
      <span />
      <span />
      <span />
    </StyledWorkingDots>
  )
}

export type AskPanelProps = {
  prompts: Array<AnalyticsMcpSample>
  selectedPromptId: string
  onSelectPrompt: (prompt: AnalyticsMcpSample) => void
  // Fires from the follow-up, which is where a visitor is most likely to want
  // the same question asked of their own numbers. Same label as the page's
  // other calls to action: one ask should not have several names.
  onRequestAccess: () => void
}

/**
 * The preview: pick a question, watch it get answered. Sample data throughout —
 * see the catalog in `domain/analyticsMcp` for why it has to be.
 */
export default function AskPanel({
  prompts,
  selectedPromptId,
  onSelectPrompt,
  onRequestAccess,
}: AskPanelProps) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const { hasReached, isComplete, replay } = useAnalyticsMcpPlayback(selectedPromptId, {
    instant: prefersReducedMotion,
  })

  const selected = prompts.find(prompt => prompt.id === selectedPromptId) ?? prompts[0]
  const groups = groupSamplePromptsByProduct(prompts)

  const { lookup, answer } = selected

  return (
    <StyledPanel>
      <StyledPanelBar>
        <Icon name="sparkle" size="16px" color="color-brand" />
        <StyledPanelTitle>Analytics MCP</StyledPanelTitle>
        <StyledSampleBadge>Sample data</StyledSampleBadge>
        <StyledPanelSpacer />
        <StyledReplayButton type="button" onClick={replay} disabled={!isComplete}>
          <Icon name="refresh" size="12px" />
          Replay
        </StyledReplayButton>
      </StyledPanelBar>

      <StyledQuestionRows>
        {groups.map(([product, groupPrompts]) => (
          <StyledQuestionRow key={product}>
            <StyledQuestionRowLabel>{analyticsMcpProductLabel(product)}</StyledQuestionRowLabel>
            {groupPrompts.map(prompt => {
              const isActive = prompt.id === selected.id

              return (
                <StyledQuestionChip
                  key={prompt.id}
                  type="button"
                  $active={isActive}
                  aria-pressed={isActive}
                  onClick={() => onSelectPrompt(prompt)}
                >
                  {prompt.question}
                </StyledQuestionChip>
              )
            })}
          </StyledQuestionRow>
        ))}
      </StyledQuestionRows>

      {/*
        One live region: the steps arrive on a timer rather than from a user
        action, so a screen reader has to be told about them.
      */}
      <StyledExchange aria-live="polite" aria-atomic="false">
        <StyledStep key={`question-${selected.id}`}>
          <StyledQuestion>{selected.question}</StyledQuestion>
        </StyledStep>

        {hasReached('reading') && (
          <StyledStep key={`reading-${selected.id}`}>
            <StyledStatusLine $tone="muted">
              {formatLookup(lookup)}
              {!hasReached('rows') && <WorkingDots />}
            </StyledStatusLine>
          </StyledStep>
        )}

        {hasReached('rows') && (
          <StyledStep key={`rows-${selected.id}`}>
            <StyledStatusLine $tone="done">
              <Icon name="check" size="12px" />
              {formatRowsReturned(lookup)}
            </StyledStatusLine>
          </StyledStep>
        )}

        {hasReached('answer') && (
          <StyledStep key={`answer-${selected.id}`}>
            <StyledAnswer>
              <StyledSummary>{answer.summary}</StyledSummary>

              <StyledTableWrapper>
                <StyledTable>
                  <thead>
                    <tr>
                      {answer.table.columns.map((column, index) => (
                        <th
                          key={column}
                          scope="col"
                          className={isMeasureColumn(index) ? 'measure' : undefined}
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {answer.table.rows.map(row => (
                      <tr key={row[0]}>
                        {row.map((cell, index) => (
                          <td
                            key={`${row[0]}-${answer.table.columns[index] ?? index}`}
                            className={isMeasureColumn(index) ? 'measure' : undefined}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </StyledTableWrapper>

              <StyledFollowUp>
                <StyledFollowUpText>{answer.followUp}</StyledFollowUpText>
                <StyledFollowUpAction type="button" onClick={onRequestAccess}>
                  <Icon name="send" size="14px" />
                  Request early access
                </StyledFollowUpAction>
              </StyledFollowUp>
            </StyledAnswer>
          </StyledStep>
        )}
      </StyledExchange>
    </StyledPanel>
  )
}
